/**
 * A form rendered from the API's own field descriptors.
 *
 * The point of typing the JSONB specs (docs/adr/0002) was that adding an
 * equipment category or event type should be one Pydantic class and no React
 * work. This is the half that makes that true — the shape of the form comes
 * from the server, so the two can't drift.
 */

import { useEffect } from "react";

import NumericInput from "@/components/NumericInput";
import type { Equipment } from "@/types";

export interface FieldDescriptor {
  name: string;
  label: string;
  type:
    "string" | "number" | "integer" | "boolean" | "enum" | "date" | "equipment";
  required: boolean;
  options: string[] | null;
  description: string | null;
  /** For `equipment` fields: which category to offer, or null for all. */
  equipment_category?: string | null;
}

/**
 * What the API publishes about one event type.
 *
 * Shared rather than restated per screen: the dialog that logs an event and the
 * editor that corrects one are rendering the same description of the same
 * thing, and two copies of this shape is two places for it to fall behind the
 * server.
 */
export interface EventSchema {
  event_type: string;
  label: string;
  is_span: boolean;
  fields: FieldDescriptor[];
}

interface Props {
  fields: FieldDescriptor[];
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  /** The vessel's equipment log, for rendering `equipment` fields. */
  equipment?: Equipment[];
}

/**
 * How one piece of equipment reads to a person.
 *
 * Exported because the event list needs the same words the picker used. A card
 * that says "Vevor UIRWAY 16x32" beside a dialog that said the same thing is
 * one fact; two spellings of it would be two.
 */
export function equipmentLabel(item: Equipment): string {
  const name = [item.manufacturer, item.model].filter(Boolean).join(" ");
  const base = name || item.category.replace(/_/g, " ");
  // Retired gear stays selectable — "which cover was on in 2027" has to keep
  // its answer after the cover is replaced, and equipment is never deleted.
  return item.is_active ? base : `${base} (retired)`;
}

export default function SchemaForm({
  fields,
  values,
  onChange,
  equipment = [],
}: Props) {
  return (
    <div className="space-y-3">
      {fields.map((field) => {
        const id = `spec-${field.name}`;
        const value = values[field.name];

        return (
          <div key={field.name}>
            <label htmlFor={id} className="text-sm font-medium">
              {field.label}
              {field.required && <span aria-hidden> *</span>}
            </label>

            <div className="mt-1.5">
              {field.type === "boolean" ? (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    id={id}
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={(e) => onChange(field.name, e.target.checked)}
                    className="size-5"
                  />
                  Yes
                </label>
              ) : field.type === "equipment" ? (
                <EquipmentField
                  id={id}
                  field={field}
                  value={value}
                  equipment={equipment}
                  onChange={onChange}
                />
              ) : field.type === "enum" ? (
                <select
                  id={id}
                  value={String(value ?? "")}
                  onChange={(e) => onChange(field.name, e.target.value || null)}
                  className="field"
                >
                  <option value="">Not set</option>
                  {(field.options ?? []).map((option) => (
                    <option key={option} value={option}>
                      {option.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              ) : field.type === "number" || field.type === "integer" ? (
                <NumericInput
                  id={id}
                  decimal={field.type === "number"}
                  value={String(value ?? "")}
                  onChange={(next) => onChange(field.name, next || null)}
                  className="field figure"
                />
              ) : (
                <input
                  id={id}
                  type="text"
                  value={String(value ?? "")}
                  onChange={(e) => onChange(field.name, e.target.value || null)}
                  className="field"
                />
              )}
            </div>

            {field.description && (
              <p className="mt-1 text-xs text-(--color-ink-faint)">
                {field.description}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * A picker over the equipment log, in place of a box asking for a UUID.
 *
 * These fields are `uuid.UUID | None` on the server, and the self-describing
 * form had no idea they referred to anything — so a solar cover event rendered
 * a text input labelled "Equipment id" with no hint of what belonged in it, and
 * no realistic way to find out. Nobody was ever going to fill that in.
 *
 * When exactly one piece of equipment matches, it selects itself. A pool with
 * one solar cover should not be asked which solar cover; the field is
 * provenance for the day there are two, and until then it should be invisible
 * work. The choice is still shown rather than hidden, so it can be corrected
 * and so the record does not acquire a link the owner never saw.
 */
function EquipmentField({
  id,
  field,
  value,
  equipment,
  onChange,
}: {
  id: string;
  field: FieldDescriptor;
  value: unknown;
  equipment: Equipment[];
  onChange: (name: string, value: unknown) => void;
}) {
  const matches = field.equipment_category
    ? equipment.filter((item) => item.category === field.equipment_category)
    : equipment;

  // Prefer something currently installed when auto-filling. A retired cover is
  // a legitimate answer for a backdated event but the wrong default for today.
  const active = matches.filter((item) => item.is_active);
  const only = active.length === 1 ? active[0] : null;

  useEffect(() => {
    if (only && !value) onChange(field.name, only.id);
  }, [only, value, field.name, onChange]);

  if (matches.length === 0) {
    return (
      <p className="text-sm text-(--color-ink-faint)">
        No {field.label.toLowerCase()} in the equipment log yet. Add one under
        Setup to link it here — the event records fine without it.
      </p>
    );
  }

  return (
    <select
      id={id}
      value={String(value ?? "")}
      onChange={(e) => onChange(field.name, e.target.value || null)}
      className="field"
    >
      <option value="">Not set</option>
      {matches.map((item) => (
        <option key={item.id} value={item.id}>
          {equipmentLabel(item)}
        </option>
      ))}
    </select>
  );
}
